'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import type {
  BodyScrollEvent,
  CellContextMenuEvent,
  ColDef,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  RowClassParams,
  RowClickedEvent,
  RowStyle,
  SortChangedEvent,
} from 'ag-grid-community';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  MetaDefault,
  Spinner,
  TABLE_IDS,
  Table,
  archivedEntityRowCss,
  isArchivedTracePreview,
} from '@terra/ui-design-system';
import { WorkHistoryIcon } from '@terra/ui-icons';
import type { TracePreviewDto } from '@terra/shared-contracts-trace';
import type {
  TraceListGrouping,
  TraceListSort,
} from '../../../store/tab/tracesTabScopedFilterStore';
import { EmptyState } from '../../EmptyState';
import {
  LIST_PAGE_TABLE_HEADER_CSS,
  LIST_PAGE_TABLE_HEADER_HEIGHT_PX,
  LIST_PAGE_TABLE_THEME_PARAMS,
} from '../../common/list-page-table-theme';
import { shouldLoadMoreListPageRows } from '../../common/list-page-infinite-table.model';
import { ListPageTableBodyLayer } from '../../common/ListPageTableBodyLayer';
import { useListPageTablePersistence } from '../../common/useListPageTablePersistence';
import { isRowClickDisabledTarget } from '../rowClickBlocker';
import { getExecutionRowId } from './executions-list.model';
import {
  EXECUTIONS_GROUP_HEADER_ROW_CLASS,
  isExecutionsGroupHeaderRow,
  isSameExecutionsSortState,
  resolveExecutionsColumnSortState,
  resolveExecutionsSortChange,
  type ExecutionsGridRow,
} from './executions-grouping.model';
import { useExecutionsTableColumnDefs } from './useExecutionsTableColumnDefs';

const EXECUTIONS_TABLE_ROW_HEIGHT_PX = 36;
const USER_SORT_SOURCE = 'uiColumnSorted';
const DEFAULT_COL_DEF: ColDef<ExecutionsGridRow> = {
  sortable: false,
  filter: false,
  resizable: true,
  lockPinned: true,
  sortingOrder: ['desc', 'asc', null],
};
const EXECUTIONS_TABLE_CSS = {
  ...LIST_PAGE_TABLE_HEADER_CSS,
  [`& .${EXECUTIONS_GROUP_HEADER_ROW_CLASS} .ag-cell`]: {
    backgroundColor: 'var(--colors-bg-subtle)',
  },
};

type ExecutionsTableProps = {
  rows: ExecutionsGridRow[];
  traceCount: number;
  loadMoreRowIndex: number | null;
  sort: TraceListSort;
  grouping: TraceListGrouping;
  collapsedGroupKeys: ReadonlySet<string>;
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  hasActiveFilters: boolean;
  onLoadMore: () => void;
  onSortChange: (change: {
    sort: TraceListSort;
    grouping: TraceListGrouping;
  }) => void;
  onToggleGroup: (groupKey: string) => void;
  onOpenTrace: (trace: TracePreviewDto) => void;
  onOpenTraceContextMenu: ({
    event,
    trace,
  }: {
    event: MouseEvent;
    trace: TracePreviewDto;
  }) => void;
};

const getExecutionsTableRowId = (params: GetRowIdParams<ExecutionsGridRow>) =>
  isExecutionsGroupHeaderRow(params.data)
    ? params.data.id
    : getExecutionRowId(params.data);

const getExecutionsTableRowStyle = (
  params: RowClassParams<ExecutionsGridRow>,
): RowStyle => {
  if (isExecutionsGroupHeaderRow(params.data)) {
    return { cursor: 'default' };
  }

  return {
    cursor: 'pointer',
    ...(params.data && isArchivedTracePreview(params.data)
      ? archivedEntityRowCss
      : {}),
  };
};

const getExecutionsTableRowClass = (
  params: RowClassParams<ExecutionsGridRow>,
): string | undefined =>
  isExecutionsGroupHeaderRow(params.data)
    ? EXECUTIONS_GROUP_HEADER_ROW_CLASS
    : undefined;

function ExecutionsTableEmptyState({
  hasActiveFilters,
}: Pick<ExecutionsTableProps, 'hasActiveFilters'>) {
  const t = useTranslations('proxy.executions.table');

  return (
    <EmptyState
      icon={WorkHistoryIcon}
      title={hasActiveFilters ? t('emptyFilteredTitle') : t('emptyTitle')}
      description={
        hasActiveFilters ? t('emptyFilteredDescription') : t('emptyDescription')
      }
    />
  );
}

export function ExecutionsTable({
  rows,
  traceCount,
  loadMoreRowIndex,
  sort,
  grouping,
  collapsedGroupKeys,
  isLoading,
  isFetchingMore,
  hasMore,
  hasActiveFilters,
  onLoadMore,
  onSortChange,
  onToggleGroup,
  onOpenTrace,
  onOpenTraceContextMenu,
}: ExecutionsTableProps) {
  const t = useTranslations('proxy.executions.table');
  const gridApiRef = useRef<GridApi<ExecutionsGridRow> | null>(null);
  const columnDefs = useExecutionsTableColumnDefs({
    grouping,
    collapsedGroupKeys,
    onToggleGroup,
  });
  const persistence = useListPageTablePersistence(
    TABLE_IDS.PROXY_EXECUTIONS_LIST,
  );
  const columnSortState = useMemo(
    () => resolveExecutionsColumnSortState({ sort, grouping }),
    [grouping, sort],
  );
  const applyColumnSortState = useCallback(
    (api: GridApi<ExecutionsGridRow>) =>
      api.applyColumnState({
        state: columnSortState,
        defaultState: { sort: null, sortIndex: null },
      }),
    [columnSortState],
  );

  useEffect(() => {
    if (gridApiRef.current && !gridApiRef.current.isDestroyed()) {
      applyColumnSortState(gridApiRef.current);
    }
  }, [applyColumnSortState]);

  const loadMoreIfNearEnd = useCallback(
    (api: GridApi<ExecutionsGridRow>) => {
      if (
        shouldLoadMoreListPageRows({
          lastDisplayedRowIndex: api.getLastDisplayedRowIndex(),
          loadMoreRowIndex,
          hasMore,
          isFetchingMore,
        })
      ) {
        onLoadMore();
      }
    },
    [hasMore, isFetchingMore, loadMoreRowIndex, onLoadMore],
  );
  const handleGridReady = useCallback(
    (event: GridReadyEvent<ExecutionsGridRow>) => {
      gridApiRef.current = event.api;
      applyColumnSortState(event.api);
    },
    [applyColumnSortState],
  );
  const handleSortChanged = useCallback(
    (event: SortChangedEvent<ExecutionsGridRow>) => {
      if (event.source !== USER_SORT_SOURCE) {
        return;
      }

      const sortedColumn = event.api
        .getColumnState()
        .find((column) => column.sort === 'asc' || column.sort === 'desc');
      const change = resolveExecutionsSortChange({
        sortedColumn: sortedColumn
          ? {
              colId: sortedColumn.colId,
              sort: sortedColumn.sort === 'asc' ? 'asc' : 'desc',
            }
          : null,
        sort,
        grouping,
      });

      if (
        isSameExecutionsSortState({
          left: change,
          right: { sort, grouping },
        })
      ) {
        applyColumnSortState(event.api);

        return;
      }

      onSortChange(change);
    },
    [applyColumnSortState, grouping, onSortChange, sort],
  );
  const handleRowClicked = useCallback(
    (event: RowClickedEvent<ExecutionsGridRow>) => {
      if (!event.data || isRowClickDisabledTarget(event.event)) {
        return;
      }

      if (isExecutionsGroupHeaderRow(event.data)) {
        onToggleGroup(event.data.groupKey);

        return;
      }

      onOpenTrace(event.data);
    },
    [onOpenTrace, onToggleGroup],
  );
  const handleCellContextMenu = useCallback(
    (event: CellContextMenuEvent<ExecutionsGridRow>) => {
      if (
        !event.data ||
        isExecutionsGroupHeaderRow(event.data) ||
        !(event.event instanceof MouseEvent)
      ) {
        return;
      }

      onOpenTraceContextMenu({ event: event.event, trace: event.data });
    },
    [onOpenTraceContextMenu],
  );

  return (
    <Box
      flex={1}
      w="full"
      h="full"
      overflow="hidden"
      position="relative"
      css={EXECUTIONS_TABLE_CSS}
    >
      <Table
        key={grouping?.field ?? 'flat'}
        columnDefs={columnDefs}
        persistence={persistence}
        rowData={rows}
        getRowId={getExecutionsTableRowId}
        rowHeight={EXECUTIONS_TABLE_ROW_HEIGHT_PX}
        headerHeight={LIST_PAGE_TABLE_HEADER_HEIGHT_PX}
        noBorder={true}
        fontSize={12}
        headerFontSize={12}
        themeParams={LIST_PAGE_TABLE_THEME_PARAMS}
        animateRows={false}
        defaultColDef={DEFAULT_COL_DEF}
        suppressCellFocus={true}
        suppressMultiSort={true}
        hideColumnControls={true}
        fitColumnsOnGridSizeChanged={false}
        onGridReady={handleGridReady}
        onSortChanged={handleSortChanged}
        onBodyScroll={(event: BodyScrollEvent<ExecutionsGridRow>) =>
          loadMoreIfNearEnd(event.api)
        }
        onRowDataUpdated={(event) => loadMoreIfNearEnd(event.api)}
        onRowClicked={handleRowClicked}
        onCellContextMenu={handleCellContextMenu}
        preventDefaultOnContextMenu
        getRowStyle={getExecutionsTableRowStyle}
        getRowClass={getExecutionsTableRowClass}
        suppressNoRowsOverlay
      />
      {traceCount === 0 && !isLoading ? (
        <ListPageTableBodyLayer>
          <ExecutionsTableEmptyState hasActiveFilters={hasActiveFilters} />
        </ListPageTableBodyLayer>
      ) : null}
      {isFetchingMore ? (
        <HStack
          position="absolute"
          bottom={3}
          left="50%"
          transform="translateX(-50%)"
          gap={2}
          px={3}
          py={1.5}
          borderRadius="full"
          bg="bg.default"
          boxShadow="sm"
          pointerEvents="none"
        >
          <Spinner size="sm" />
          <MetaDefault color="fg.muted">{t('loadingMore')}</MetaDefault>
        </HStack>
      ) : null}
    </Box>
  );
}
